import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/v1`;

// API client for AgentCheck
export const api = {
  // Create a new scan
  createScan: async (url) => {
    const response = await axios.post(`${API_BASE}/scan`, { url });
    return response.data;
  },

  // Get scan status
  getScanStatus: async (scanId) => {
    const response = await axios.get(`${API_BASE}/scan/${scanId}`);
    return response.data;
  },

  // Get public report
  getReport: async (publicReportId) => {
    const response = await axios.get(`${API_BASE}/report/${publicReportId}`);
    return response.data;
  },

  // Create share card
  createShareCard: async (publicReportId) => {
    const response = await axios.post(`${API_BASE}/sharecard`, {
      public_report_id: publicReportId
    });
    return response.data;
  },

  // Get share card image URL
  getShareCardImageUrl: (publicReportId) => {
    return `${API_BASE}/sharecard/image/${publicReportId}`;
  },

  // Get categories info
  getCategories: async () => {
    const response = await axios.get(`${API_BASE}/categories`);
    return response.data;
  },

  // Get user entitlements
  getEntitlements: async (deviceId) => {
    const response = await axios.get(`${API_BASE}/me/entitlements`, {
      params: { device_id: deviceId },
    });
    return response.data;
  },

  // Get AI Bot Leaderboard
  getLeaderboard: async (category) => {
    const params = category ? { category } : {};
    const response = await axios.get(`${API_BASE}/leaderboard/ai-bots`, { params });
    return response.data;
  },

  // Create billing portal session
  createBillingPortal: async (deviceId) => {
    const response = await axios.post(`${API_BASE}/billing/portal`, { device_id: deviceId });
    return response.data;
  },

  // Get leaderboard deltas
  getLeaderboardDeltas: async () => {
    const response = await axios.get(`${API_BASE}/leaderboard/deltas`);
    return response.data;
  },

  // Community domain submissions
  submitDomain: async (domain) => {
    const response = await axios.post(`${API_BASE}/community/submit-domain`, { domain });
    return response.data;
  },

  getCommunitySubmissions: async (limit = 20) => {
    const response = await axios.get(`${API_BASE}/community/submissions`, { params: { limit } });
    return response.data;
  },

  getSubmissionStatus: async (submissionId) => {
    const response = await axios.get(`${API_BASE}/community/submissions/${submissionId}`);
    return response.data;
  },

  // Posture vs Activity comparison
  getPostureComparison: async (domain) => {
    const response = await axios.get(`${API_BASE}/verify/posture/${encodeURIComponent(domain)}`);
    return response.data;
  },

  // Defense tools
  generateRobotsTxt: async (options) => {
    const response = await axios.post(`${API_BASE}/defense/robots-txt`, options);
    return response.data;
  },

  getBlocklist: async (listType, format = 'hosts') => {
    const response = await axios.get(`${API_BASE}/defense/blocklist/${listType}`, {
      params: { format },
      responseType: 'text',
    });
    return response.data;
  },

  generateHoneypot: async (domain) => {
    const response = await axios.post(`${API_BASE}/defense/honeypot`, { domain });
    return response.data;
  },

  generateAiPolicy: async (options) => {
    const response = await axios.post(`${API_BASE}/defense/ai-policy`, options);
    return response.data;
  },

  getBotsCatalog: async () => {
    const response = await axios.get(`${API_BASE}/defense/bots-catalog`);
    return response.data;
  },

  // rDNS verification
  verifyRDNS: async (ip, claimedBot) => {
    const response = await axios.post(`${API_BASE}/verify/rdns`, {
      ip,
      claimed_bot: claimedBot,
    });
    return response.data;
  },

  // Alert management
  getAlertConfig: async () => {
    const response = await axios.get(`${API_BASE}/alerts/config`);
    return response.data;
  },

  createAlertRule: async (rule) => {
    const response = await axios.post(`${API_BASE}/alerts/rules`, rule);
    return response.data;
  },

  listAlertRules: async () => {
    const response = await axios.get(`${API_BASE}/alerts/rules`);
    return response.data;
  },

  deleteAlertRule: async (ruleId) => {
    const response = await axios.delete(`${API_BASE}/alerts/rules/${ruleId}`);
    return response.data;
  },

  testAlert: async (options) => {
    const response = await axios.post(`${API_BASE}/alerts/test`, options);
    return response.data;
  },

  // Agent Control Plane
  getAgentsDashboard: async (params = {}) => {
    const response = await axios.get(`${API_BASE}/agents/dashboard`, { params });
    return response.data;
  },

  registerAgent: async (agentData) => {
    const response = await axios.post(`${API_BASE}/agents/register`, agentData);
    return response.data;
  },

  registerExternalAgent: async (agentData) => {
    const response = await axios.post(`${API_BASE}/agents/external`, agentData);
    return response.data;
  },

  getAgent: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}`);
    return response.data;
  },

  updateAgentState: async (agentId, state, reason = '') => {
    const response = await axios.put(`${API_BASE}/agents/${agentId}/state`, { state, reason });
    return response.data;
  },

  sendHeartbeat: async (agentId, apiKey, state, metrics = {}) => {
    const response = await axios.post(`${API_BASE}/agents/heartbeat`, {
      agent_id: agentId,
      api_key: apiKey,
      state,
      metrics,
    });
    return response.data;
  },

  sendTelemetry: async (event) => {
    const response = await axios.post(`${API_BASE}/agents/telemetry`, event);
    return response.data;
  },

  getAgentTelemetry: async (agentId, limit = 50) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/telemetry`, { params: { limit } });
    return response.data;
  },

  deregisterAgent: async (agentId) => {
    const response = await axios.delete(`${API_BASE}/agents/${agentId}`);
    return response.data;
  },

  // Phase 3: Log ingestion + Identity
  parseLogLine: async (line) => {
    const response = await axios.post(`${API_BASE}/logs/parse`, { line });
    return response.data;
  },

  ingestLogs: async (lines, options = {}) => {
    const response = await axios.post(`${API_BASE}/logs/ingest`, {
      lines,
      register_agents: options.registerAgents ?? true,
      source: options.source || 'manual',
    });
    return response.data;
  },

  getIdentityProviders: async () => {
    const response = await axios.get(`${API_BASE}/identity/providers`);
    return response.data;
  },

  resolveIdentity: async (identifier) => {
    const response = await axios.post(`${API_BASE}/identity/resolve`, { identifier });
    return response.data;
  },

  resolveAgentOwner: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/resolve-owner`);
    return response.data;
  },

  // Phase 4: TLS / Shadow AI
  inspectTLS: async (clientHelloHex) => {
    const response = await axios.post(`${API_BASE}/tls/inspect`, {
      client_hello_hex: clientHelloHex,
    });
    return response.data;
  },

  getShadowAICatalog: async () => {
    const response = await axios.get(`${API_BASE}/shadow-ai/catalog`);
    return response.data;
  },

  detectShadowAI: async (hostnames) => {
    const response = await axios.post(`${API_BASE}/shadow-ai/detect`, { hostnames });
    return response.data;
  },

  ingestShadowAI: async (hostnames) => {
    const response = await axios.post(`${API_BASE}/shadow-ai/ingest`, { hostnames });
    return response.data;
  },

  // Phase 5: Enforcement / Kill switch
  killAgent: async (agentId, options = {}) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/kill`, options);
    return response.data;
  },

  rotateAgentKey: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/rotate-key`);
    return response.data;
  },

  generateEnforcement: async (agentId, target) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/enforce`, { target });
    return response.data;
  },

  listEnforcementActions: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/enforcement`);
    return response.data;
  },

  // Phase 5: Organizations / Departments / RBAC
  createOrg: async (data) => {
    const response = await axios.post(`${API_BASE}/orgs`, data);
    return response.data;
  },

  getOrg: async (orgId) => {
    const response = await axios.get(`${API_BASE}/orgs/${orgId}`);
    return response.data;
  },

  listOrgs: async () => {
    const response = await axios.get(`${API_BASE}/orgs`);
    return response.data;
  },

  createDepartment: async (orgId, data) => {
    const response = await axios.post(`${API_BASE}/orgs/${orgId}/departments`, data);
    return response.data;
  },

  listDepartments: async (orgId) => {
    const response = await axios.get(`${API_BASE}/orgs/${orgId}/departments`);
    return response.data;
  },

  inviteMember: async (orgId, data) => {
    const response = await axios.post(`${API_BASE}/orgs/${orgId}/members`, data);
    return response.data;
  },

  listMembers: async (orgId) => {
    const response = await axios.get(`${API_BASE}/orgs/${orgId}/members`);
    return response.data;
  },

  updateMemberRole: async (orgId, userId, role) => {
    const response = await axios.put(`${API_BASE}/orgs/${orgId}/members/${userId}`, { role });
    return response.data;
  },

  removeMember: async (orgId, userId) => {
    const response = await axios.delete(`${API_BASE}/orgs/${orgId}/members/${userId}`);
    return response.data;
  },

  // API docs (OpenAPI spec)
  getOpenAPISpec: async () => {
    const response = await axios.get(`${BACKEND_URL}/openapi.json`);
    return response.data;
  },

  // Phase 6: Hunter Agent
  startHunterRun: async (source = 'both', orgId = null) => {
    const data = { source };
    if (orgId) data.org_id = orgId;
    const response = await axios.post(`${API_BASE}/agents/hunter/run`, data);
    return response.data;
  },

  getHunterRun: async (runId) => {
    const response = await axios.get(`${API_BASE}/agents/hunter/run/${runId}`);
    return response.data;
  },

  getHunterFindings: async (limit = 50, severity = null, orgId = null) => {
    const params = { limit };
    if (severity) params.severity = severity;
    if (orgId) params.org_id = orgId;
    const response = await axios.get(`${API_BASE}/agents/hunter/findings`, { params });
    return response.data;
  },

  createHunterSchedule: async (cron, enabled = true, orgId = null) => {
    const data = { cron, enabled };
    if (orgId) data.org_id = orgId;
    const response = await axios.post(`${API_BASE}/agents/hunter/schedule`, data);
    return response.data;
  },

  getHunterSchedules: async () => {
    const response = await axios.get(`${API_BASE}/agents/hunter/schedules`);
    return response.data;
  },

  // Phase 6: LLM Safety Engine
  scanLLMSafety: async (text, direction = 'input', redact = false) => {
    const response = await axios.post(`${API_BASE}/llm-safety/scan`, { text, direction, redact });
    return response.data;
  },

  scanLLMSafetyBatch: async (texts, direction = 'input', redact = false) => {
    const response = await axios.post(`${API_BASE}/llm-safety/scan/batch`, { texts, direction, redact });
    return response.data;
  },

  getLLMSafetyPatterns: async () => {
    const response = await axios.get(`${API_BASE}/llm-safety/patterns`);
    return response.data;
  },

  // Phase 6: Stack-Deploy
  validateStackManifest: async (yamlStr) => {
    const response = await axios.post(`${API_BASE}/stack-deploy/validate`, { yaml: yamlStr });
    return response.data;
  },

  applyStackManifest: async (yamlStr) => {
    const response = await axios.post(`${API_BASE}/stack-deploy/apply`, { yaml: yamlStr });
    return response.data;
  },

  getCurrentManifest: async (orgId) => {
    const response = await axios.get(`${API_BASE}/stack-deploy/current/${orgId}`);
    return response.data;
  },

  analyzeStackManifest: async (yamlStr) => {
    const response = await axios.post(`${API_BASE}/stack-deploy/analyze`, { yaml: yamlStr });
    return response.data;
  },

  // Phase 7: Policy-Based Execution (Gap #1)
  createPolicySet: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/policies`, data);
    return response.data;
  },

  getAgentPolicies: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/policies`);
    return response.data;
  },

  evaluateAction: async (agentId, resource, action, context = {}) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/policies/evaluate`, { resource, action, context });
    return response.data;
  },

  // Phase 7: Zero-Trust Inter-Agent (Gap #2)
  issueSigningKey: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/signing-key`);
    return response.data;
  },

  verifyAgentMessage: async (signedMessage) => {
    const response = await axios.post(`${API_BASE}/agents/trust/verify`, { signed_message: signedMessage });
    return response.data;
  },

  getAgentTrustScore: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/trust-score`);
    return response.data;
  },

  // Phase 7: Token Circuit Breakers (Gap #3)
  createAgentBudget: async (agentId, limits = {}) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/budget`, limits);
    return response.data;
  },

  getAgentBudget: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/budget`);
    return response.data;
  },

  checkAgentBudget: async (agentId, data = {}) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/budget/check`, data);
    return response.data;
  },

  resetAgentBreaker: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/budget/reset`);
    return response.data;
  },

  estimateCost: async (model, inputTokens, outputTokens) => {
    const response = await axios.post(`${API_BASE}/cost/estimate`, { model, input_tokens: inputTokens, output_tokens: outputTokens });
    return response.data;
  },

  // Phase 7: Agent Lifecycle Governance (Gap #4)
  startOffboarding: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/lifecycle/offboard`);
    return response.data;
  },

  getAgentLifecycle: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/lifecycle`);
    return response.data;
  },

  registerMemoryInventory: async (agentId, locations) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/memory-inventory`, { locations });
    return response.data;
  },

  generateWipeCertificate: async (agentId, wipedLocationIds) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/lifecycle/wipe-certificate`, { wiped_location_ids: wipedLocationIds });
    return response.data;
  },

  // Phase 7: Agent SBOM (Gap #5)
  createAgentSBOM: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/sbom`, data);
    return response.data;
  },

  getAgentSBOM: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/sbom`);
    return response.data;
  },

  scanAgentSBOM: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/sbom/scan`);
    return response.data;
  },

  // Phase 8: AI Doctor — Room 1 (ER)
  triageAgent: async (agentId, data = {}) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/triage`, data);
    return response.data;
  },
  stabilizeAgent: async (agentId) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/stabilize`);
    return response.data;
  },
  configureGuardrail: async (agentId, provider, settings) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/guardrail`, { provider, settings });
    return response.data;
  },
  getGuardrailProviders: async () => {
    const response = await axios.get(`${API_BASE}/doctor/guardrail-providers`);
    return response.data;
  },

  // Phase 8: AI Doctor — Room 2 (Radiology)
  startTrace: async (agentId, taskName) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/trace`, { task_name: taskName });
    return response.data;
  },
  addSpan: async (traceId, spanType, name, inputData, parentSpanId) => {
    const response = await axios.post(`${API_BASE}/doctor/traces/${traceId}/spans`, {
      span_type: spanType, name, input_data: inputData, parent_span_id: parentSpanId,
    });
    return response.data;
  },
  completeSpan: async (traceId, spanId, data) => {
    const response = await axios.post(`${API_BASE}/doctor/traces/${traceId}/spans/${spanId}/complete`, data);
    return response.data;
  },
  getTraceXray: async (traceId) => {
    const response = await axios.get(`${API_BASE}/doctor/traces/${traceId}/xray`);
    return response.data;
  },
  getHealthSnapshot: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/doctor/health-snapshot`);
    return response.data;
  },

  // Phase 8: AI Doctor — Room 3 (Toxicology)
  runRAGDiagnostic: async (question, response, sourceDocuments, expectedFacts) => {
    const res = await axios.post(`${API_BASE}/doctor/rag-diagnostic`, {
      question, response, source_documents: sourceDocuments, expected_facts: expectedFacts,
    });
    return res.data;
  },
  createLineageRecord: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/lineage`, data);
    return response.data;
  },
  checkDataDrift: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/lineage/drift-check`, data);
    return response.data;
  },

  // Phase 8: AI Doctor — Room 4 (Red Team)
  getAttackCatalog: async () => {
    const response = await axios.get(`${API_BASE}/doctor/red-team/catalog`);
    return response.data;
  },
  startRedTeamRun: async (agentId, categories, severityMin) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/red-team/run`, {
      categories, severity_min: severityMin,
    });
    return response.data;
  },
  getRedTeamRun: async (runId) => {
    const response = await axios.get(`${API_BASE}/doctor/red-team/run/${runId}`);
    return response.data;
  },
  evaluateDefense: async (runId, vectorId, responseText, wasBlocked) => {
    const response = await axios.post(`${API_BASE}/doctor/red-team/run/${runId}/evaluate`, {
      vector_id: vectorId, response: responseText, was_blocked: wasBlocked,
    });
    return response.data;
  },
  getRedTeamSummary: async (runId) => {
    const response = await axios.get(`${API_BASE}/doctor/red-team/run/${runId}/summary`);
    return response.data;
  },

  // Phase 8: AI Doctor — Room 5 (Incidents / Health Records)
  createIncident: async (agentId, severity, title, description, reporter) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/doctor/incident`, {
      severity, title, description, reporter,
    });
    return response.data;
  },
  getAgentIncidents: async (agentId, status) => {
    const url = status ? `${API_BASE}/agents/${agentId}/doctor/incidents?status=${status}` : `${API_BASE}/agents/${agentId}/doctor/incidents`;
    const response = await axios.get(url);
    return response.data;
  },
  getIncident: async (incidentId) => {
    const response = await axios.get(`${API_BASE}/doctor/incidents/${incidentId}`);
    return response.data;
  },
  getIncidentSeverities: async () => {
    const response = await axios.get(`${API_BASE}/doctor/incident-severities`);
    return response.data;
  },
  getHealthRecord: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/doctor/health-record`);
    return response.data;
  },

  // ── AI Debt Control Tower ──────────────────────────────────────

  recordIntelligenceAsset: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/intelligence/asset`, data);
    return response.data;
  },
  recordIntelligenceLiability: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/intelligence/liability`, data);
    return response.data;
  },
  getBalanceSheet: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/balance-sheet`);
    return response.data;
  },
  getIntelligenceYield: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/yield`);
    return response.data;
  },
  getDebtTrend: async (agentId, windowDays = 30) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/trend?window_days=${windowDays}`);
    return response.data;
  },
  getOrgBalanceSheet: async (orgId) => {
    const response = await axios.get(`${API_BASE}/orgs/${orgId}/intelligence/balance-sheet`);
    return response.data;
  },
  getIntelligenceGrades: async () => {
    const response = await axios.get(`${API_BASE}/intelligence/grades`);
    return response.data;
  },
  scoreContextDocument: async (data) => {
    const response = await axios.post(`${API_BASE}/intelligence/context/score`, data);
    return response.data;
  },
  detectContextConflicts: async (documents) => {
    const response = await axios.post(`${API_BASE}/intelligence/context/conflicts`, { documents });
    return response.data;
  },
  scoreRetrievalQuality: async (data) => {
    const response = await axios.post(`${API_BASE}/intelligence/context/retrieval-quality`, data);
    return response.data;
  },
  getContextTrust: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/context-trust`);
    return response.data;
  },
  createEvalSuite: async (data) => {
    const response = await axios.post(`${API_BASE}/intelligence/eval/suite`, data);
    return response.data;
  },
  runEvalSuite: async (suiteId, outputs) => {
    const response = await axios.post(`${API_BASE}/intelligence/eval/run`, { suite_id: suiteId, outputs });
    return response.data;
  },
  getEvalRun: async (runId) => {
    const response = await axios.get(`${API_BASE}/intelligence/eval/run/${runId}`);
    return response.data;
  },
  getEvalRegression: async (suiteId) => {
    const response = await axios.get(`${API_BASE}/intelligence/eval/regression/${suiteId}`);
    return response.data;
  },
  recordTransaction: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/intelligence/transaction`, data);
    return response.data;
  },
  getFinOpsReport: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/finops`);
    return response.data;
  },
  getWasteRatio: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/waste`);
    return response.data;
  },
  getOptimizations: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/optimizations`);
    return response.data;
  },
  recordBaseline: async (agentId, data) => {
    const response = await axios.post(`${API_BASE}/agents/${agentId}/intelligence/baseline`, data);
    return response.data;
  },
  checkDrift: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/drift`);
    return response.data;
  },
  checkCostRegression: async (agentId) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/cost-regression`);
    return response.data;
  },
  compileEvidence: async (data) => {
    const response = await axios.post(`${API_BASE}/intelligence/evidence/compile`, data);
    return response.data;
  },
  validateEvidence: async (bundleId) => {
    const response = await axios.post(`${API_BASE}/intelligence/evidence/validate`, { bundle_id: bundleId });
    return response.data;
  },
  getEvidenceManifest: async (bundleId) => {
    const response = await axios.get(`${API_BASE}/intelligence/evidence/manifest/${bundleId}`);
    return response.data;
  },
  recordSafetyEvent: async (data) => {
    const response = await axios.post(`${API_BASE}/intelligence/events`, data);
    return response.data;
  },
  getEventSummary: async (windowHours = 24) => {
    const response = await axios.get(`${API_BASE}/intelligence/events/summary?window_hours=${windowHours}`);
    return response.data;
  },
  getAgentEvents: async (agentId, limit = 50) => {
    const response = await axios.get(`${API_BASE}/agents/${agentId}/intelligence/events?limit=${limit}`);
    return response.data;
  },
};

// Utility functions
export const formatNumber = (num) => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num.toString();
};

export const getScoreColor = (score) => {
  if (score <= 25) return '#10B981'; // Green - Low risk
  if (score <= 50) return '#F59E0B'; // Amber - Medium risk
  if (score <= 75) return '#F97316'; // Orange - High risk
  return '#EF4444'; // Red - Very high risk
};

export const getScoreLabel = (score) => {
  if (score <= 25) return 'Low';
  if (score <= 50) return 'Moderate';
  if (score <= 75) return 'High';
  return 'Very High';
};

export const getCategoryLabel = (category) => {
  const labels = {
    ADS: 'Advertising',
    ANALYTICS: 'Analytics',
    SOCIAL: 'Social Media',
    CHAT: 'Chat Widget',
    SESSION_REPLAY: 'Session Replay',
    PAYMENT: 'Payment',
    CDP: 'Customer Data',
    INFRA: 'Infrastructure',
    FRAUD: 'Fraud Prevention',
    AI_AGENT: 'AI Agent',
    UNKNOWN: 'Unknown'
  };
  return labels[category] || category;
};

export const getCategoryColor = (category) => {
  const colors = {
    ADS: '#EF4444',
    ANALYTICS: '#F59E0B',
    SOCIAL: '#3B82F6',
    CHAT: '#10B981',
    SESSION_REPLAY: '#EF4444',
    PAYMENT: '#10B981',
    CDP: '#F59E0B',
    INFRA: '#64748B',
    FRAUD: '#10B981',
    AI_AGENT: '#A855F7',
    UNKNOWN: '#94A3B8'
  };
  return colors[category] || '#94A3B8';
};
